import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import HighlightButton, { SecondaryButton } from '../HighlightButton/HighlightButton';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { useFileDownload } from '../../hooks/useFileDownload';
import { useCheckoutSession } from './hooks/useCheckoutSession';
import { ERROR_MESSAGES } from './utils/constants';
import { API_ENDPOINTS } from './utils/constants';
import styles from '../../css/Store.module.css';

export const ReturnPage = () => {
  const navigate = useNavigate();
  const { status, customerEmail, purchasedProduct, sessionId } = useCheckoutSession();
  const { isDownloading, downloadError, downloadAllFiles, clearDownloadError } = useFileDownload();
  const [emailSent, setEmailSent] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  // At the moment, simply have emails fail silently (in console only)

  const handleBackToCatalog = () => {
    navigate('/work');
  };

  const handleDownloadAll = async () => {
    if (!purchasedProduct || !sessionId) return;

    try {
      await downloadAllFiles(purchasedProduct, sessionId);
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const sendEmail = useCallback(async () => {
    if (!sessionId || emailSent || emailSending) return;

    setEmailSending(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}${API_ENDPOINTS.SEND_EMAIL}/${sessionId}`);
      const data = await response.json();

      if (data.success) {
        setEmailSent(true);
        console.log('Email sent successfully');
      } else {
        console.error('Email sending failed:', data.error);
      }
    } catch (error) {
      console.error('Error sending email:', error);
    } finally {
      setEmailSending(false);
    }
  }, [sessionId, emailSent, emailSending, setEmailSending, setEmailSent]);

  // Automatically send email when purchase is complete
  useEffect(() => {
    if (status === 'complete' && sessionId && customerEmail && !emailSent && !emailSending) {
      sendEmail();
    }
  }, [status, sessionId, customerEmail, emailSent, emailSending, sendEmail]);

  if (status === 'open') {
    return <Navigate to="/checkout" />;
  }

  if (status === 'error') {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.errorContainer}>
          <h2>Verification Error</h2>
          <p className={styles.errorMessage}>
            {ERROR_MESSAGES.VERIFICATION_ERROR}
          </p>
          <p>If you've completed your purchase, please check your email <strong>{customerEmail}</strong> for download links or contact our support team.</p>

          <div className={styles.errorActions}>
            <SecondaryButton onClick={handleBackToCatalog}>
              Back to Catalog
            </SecondaryButton>

            <SecondaryButton onClick={() => window.location.href = '/contact'}>
              Contact Support
            </SecondaryButton>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'complete') {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.successContainer}>
          <div className={styles.downloadSection}>
            <p>Purchase successful! Thank you for purchasing <strong>{purchasedProduct?.title || 'my music'}</strong>.</p>

            <HighlightButton onClick={handleDownloadAll} disabled={isDownloading || !purchasedProduct}>
              {isDownloading ? <div style={{ padding: 5 }}>Downloading...</div> : <div style={{ padding: 5, textTransform: 'uppercase' }}><strong>Download Files</strong></div>}
            </HighlightButton>

            {downloadError && (
              <div className={styles.errorMessage}>
                <p>{downloadError}</p>
                <SecondaryButton onClick={clearDownloadError}>Dismiss</SecondaryButton>
              </div>
            )}

            <p className={styles.emailSection}>{emailSent ? 'An email with your download links has also been sent to' : 'You will also receive a receipt at'}
              <strong> {customerEmail}</strong>. Please check your spam or trash folder if you cannot find it.
            </p>
          </div>

          <div className={styles.helpSection}>
            <p>If you have any questions or issues with your download, please <a href="/contact" target="_blank" rel="noopener noreferrer">contact me</a>.</p>
            <SecondaryButton onClick={handleBackToCatalog}>Back to Catalog</SecondaryButton>
          </div>
        </div>
      </div >
    );
  }

  // Loading state
  return (
    <div className={styles.pageContainer}>
      <LoadingSpinner message="Completing your purchase..." />
    </div>
  );
};

export default ReturnPage;
